import { AccountIdentifier } from '@icp-sdk/canisters/ledger/icp'
import { Principal } from '@icp-sdk/core/principal'
import { useEffect, useState } from 'react'
import LedgerScannerClient from '../clients/LedgerScannerClient'
import { ICP_LEDGER, TCYCLES_CANISTER_ID } from '../constants/canisterIds'
import { burnXTC, sendNFT, sendOrdinal } from '../handlers/walletActions/wallet'
import { BTCNFT, NFT } from '../interfaces/common'
import { GAEventName } from '../interfaces/googleAnalytics'
import { UTXO } from '../interfaces/walletActions'
import { logger } from '../logger'
import { TokenBalance } from '../providers/BalanceProvider'
import { useGoogleAnalytics } from '../providers/GoogleAnalyticsProvider'
import { useContacts, useNftBalances, useWalletProperties } from '../state'
import { sendToken } from '../utils/sendToken'
import { e8sToDecimals, validatePrincipal } from '../utils/validationHelper'
import useBalances from './useBalances'
import useCanisterManualTopup from './useCanisterManualTopup'
import useMintCycles from './useMintCycles'
import { Fee, SendMode } from './useSend'
import useWallet from './useWallet'

const SUCCESS_TIMEOUT = 3000

interface Props {
  amount: string
  token?: TokenBalance
  nft?: NFT
  toAddress: string
  fee?: Fee
  utxos: UTXO[]
  xtcSendMode?: string
  onError: (error: string) => void
  goBack: () => void
  mode?: SendMode
}

const useSendReview = ({
  amount,
  token,
  nft,
  toAddress,
  fee,
  utxos,
  xtcSendMode,
  onError,
  goBack,
  mode,
}: Props) => {
  const [sendInProgress, setSendInProgress] = useState(false)
  const [sendSuccess, setSendSuccess] = useState(false)
  const [warnings, setWarnings] = useState<Set<string>>(new Set())
  const [isReady, setIsReady] = useState(false)

  const { wallet } = useWallet()
  const { getContactsByAddress } = useContacts()
  const { walletNames } = useWalletProperties()
  const { fireEvent } = useGoogleAnalytics()
  const { removeNft } = useNftBalances()
  const { refreshBalance } = useBalances()

  const otherWalletName = walletNames[toAddress]
  const contacts = getContactsByAddress(toAddress)
  const knownContact = otherWalletName || contacts[0]?.name

  const { performCyclesMint, setInputAmount: setCyclesInputAmount } = useMintCycles()
  const { topUp } = useCanisterManualTopup()

  useEffect(() => {
    mode === SendMode.CycleTopup &&
      token &&
      setCyclesInputAmount(e8sToDecimals(amount, token.token.decimals).toString())
  }, [amount, mode])

  const fetchUserActivity = async () => {
    await LedgerScannerClient.refreshTransactions(wallet).catch(console.error)

    try {
      let activity = await LedgerScannerClient.fetchUserActivity(wallet, 1, 1, {
        receiver: toAddress,
      })

      if (!activity[0] && validatePrincipal(toAddress)) {
        const accountId = AccountIdentifier.fromPrincipal({
          principal: Principal.fromText(toAddress),
        }).toHex()

        activity = await LedgerScannerClient.fetchUserActivity(wallet, 1, 1, {
          receiver: accountId,
        })
      }

      if (!activity[0]) {
        setWarnings(new Set(warnings.add('firstTimeSend')))
      }
    } catch (e) {
      setWarnings(new Set(warnings.add('failedFirstTimeSendCheck')))
      logger.debug('Failed to fetch user activity', { originalError: e })
    }
  }

  useEffect(() => {
    const validateSend = async () => {
      if (mode === SendMode.Send) {
        await fetchUserActivity()
      }

      setIsReady(true)
    }

    validateSend()
  }, [])

  const performOrdinalTransfer = async () => {
    if (!fee?.amount) return Promise.reject('No fee data available')

    const ordinal = nft as BTCNFT
    const ordinalUtxo = utxos.find(
      (u) => u.mintTxid === ordinal.txId && u.mintIndex === ordinal.mintIndex
    )
    if (!ordinalUtxo) return Promise.reject('No ordinal in utxos')

    const feeUtxo = utxos
      .filter(
        (utxo) => !(utxo.mintTxid === ordinalUtxo.mintTxid && utxo.mintIndex === ordinal.mintIndex)
      )
      .sort((a, b) => a.value - b.value)

    const sendResponse = await sendOrdinal({
      to: toAddress,
      fee: fee.amount.toString(),
      ordinalUtxo,
      feeUtxo: feeUtxo[feeUtxo.length - 1],
    })

    return sendResponse
  }

  const performICPCycleTopup = async () => {
    const cyclesMinted = await performCyclesMint()
    return topUp(`${cyclesMinted}`, toAddress)
  }

  const performPay = async () => {
    let send
    if (
      token?.token.standard === 'XTC' &&
      (xtcSendMode === 'burn' || mode === SendMode.CycleTopup)
    ) {
      send = burnXTC({ principal: toAddress, amount })
    } else if (token?.token.address === ICP_LEDGER && mode === SendMode.CycleTopup) {
      send = performICPCycleTopup()
    } else if (token?.token.address === TCYCLES_CANISTER_ID && mode === SendMode.CycleTopup) {
      send = topUp(e8sToDecimals(amount, token?.token.decimals).toString(), toAddress)
    } else if (nft?.standard == 'ordinal') {
      send = performOrdinalTransfer()
    } else if (nft) {
      send = sendNFT({
        canisterId: nft.collection,
        receiverPrincipal: toAddress,
        nftIndex: nft.index.toString(),
        standard: nft.standard,
      })
    } else if (token) {
      send = sendToken(token.token, toAddress, BigInt(amount), wallet)
    } else {
      return
    }

    setSendInProgress(true)

    try {
      const result: any = await send

      if (result?.error) {
        onError(result.error.toString())
        return
      }

      setSendSuccess(true)

      if (token) {
        fireEvent(GAEventName.SEND_TOKEN, {
          token: token.token.symbol,
          amount: Number(amount) * 10 ** -token.token.decimals,
        })
      } else if (nft) {
        fireEvent(GAEventName.SEND_NFT, {
          nft: nft.standard === 'ordinal' ? 'ordinal' : nft.collection,
        })
      }
    } catch (e) {
      onError((e as Error).toString())
      logger.error('Failed to send tokens', { originalError: e, token })
      return
    }

    setSendInProgress(false)

    setTimeout(() => {
      if (nft) {
        removeNft(wallet.principalId, nft)
      } else if (token) {
        refreshBalance(token.token)
      }

      goBack()
    }, SUCCESS_TIMEOUT)
  }

  return {
    sendInProgress,
    sendSuccess,
    performPay,
    knownContact,
    warnings,
    isReady,
  }
}

export default useSendReview
